import { oc } from "@orpc/contract";
import { z } from "zod";

const cidrSchema = z
  .string()
  .regex(
    /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/,
    "Must be a valid IPv4 CIDR (e.g. 10.0.0.0/8)",
  );

export const sandboxConfigBodySchema = z.object({
  network: z.object({
    blockAll: z.boolean(),
    allowList: z.array(cidrSchema).max(10),
    public: z.boolean().default(false),
  }),
  resources: z
    .object({
      cpu: z.number().min(1).max(4).default(1),
      memory: z.number().min(1).max(8).default(1),
      disk: z.number().min(1).max(10).default(3),
    })
    .default({ cpu: 1, memory: 1, disk: 3 }),
  lifecycle: z
    .object({
      autoStopInterval: z.number().min(0).default(15),
      autoArchiveInterval: z.number().min(0).default(10080),
      autoDeleteInterval: z.number().min(-1).default(-1),
      ephemeral: z.boolean().default(false),
    })
    .default({
      autoStopInterval: 15,
      autoArchiveInterval: 10080,
      autoDeleteInterval: -1,
      ephemeral: false,
    }),
  envVars: z.record(z.string(), z.string()).default({}),
  labels: z.record(z.string(), z.string()).default({}),
});

export type SandboxConfigBodyInput = z.infer<typeof sandboxConfigBodySchema>;

const sandboxConfigOutputSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  config: sandboxConfigBodySchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type SandboxConfigOutput = z.infer<typeof sandboxConfigOutputSchema>;

const listContract = oc
  .output(z.array(sandboxConfigOutputSchema))
  .route({ method: "GET", summary: "List sandbox configurations" });

const getContract = oc
  .input(z.object({ id: z.string().uuid() }))
  .output(sandboxConfigOutputSchema)
  .route({ method: "GET", summary: "Get sandbox configuration" });

const createContract = oc
  .input(
    z.object({
      name: z.string().min(1).max(100),
      config: sandboxConfigBodySchema,
    }),
  )
  .output(sandboxConfigOutputSchema)
  .route({ summary: "Create sandbox configuration" });

const updateContract = oc
  .input(
    z.object({
      id: z.string().uuid(),
      name: z.string().min(1).max(100).optional(),
      config: sandboxConfigBodySchema.optional(),
    }),
  )
  .output(sandboxConfigOutputSchema)
  .route({ summary: "Update sandbox configuration" });

const deleteContract = oc
  .input(z.object({ id: z.string().uuid() }))
  .output(z.object({ success: z.boolean() }))
  .route({ summary: "Delete sandbox configuration" });

export const sandboxContracts = {
  list: listContract,
  get: getContract,
  create: createContract,
  update: updateContract,
  delete: deleteContract,
};
